import { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Link,
    Routes,
    Route,
    useNavigate,
    useLocation,
} from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faMagnifyingGlass,
    faSun,
    faMoon,
} from '@fortawesome/free-solid-svg-icons'
import useAuth from '../../hooks/useAuth'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { changeTheme } from '../../features/theme/theme.slice'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { searchKb } from './api/knowledgeBaseApi'
import type { KbSearchResult } from './types'
import HomePage from './pages/HomePage'
import CategoryPage from './pages/CategoryPage'
import ArticlePage from './pages/ArticlePage'
import SearchPage from './pages/SearchPage'
import SearchOverlay from './components/SearchOverlay'
import KBFooter from './components/KBFooter'

const KnowledgeBase = () => {
    const { auth }: any = useAuth()
    const theme = useAppSelector((state) => state.theme.theme)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const axiosPrivate = useAxiosPrivate()

    const [searchOverlayOpen, setSearchOverlayOpen] = useState(false)

    const isHome = location.pathname === '/help' || location.pathname === '/help/'

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setSearchOverlayOpen((prev) => !prev)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    const goHome = useCallback(() => {
        navigate('/help')
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [navigate])

    const selectCategory = useCallback(
        (slug: string) => {
            navigate(`/help/category/${slug}`)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        [navigate]
    )

    const selectArticle = useCallback(
        (slug: string) => {
            navigate(`/help/article/${slug}`)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        [navigate]
    )

    const handleSearchSubmit = useCallback(
        (query: string) => {
            if (query.trim()) {
                navigate(`/help/search?q=${encodeURIComponent(query.trim())}`)
            }
        },
        [navigate]
    )

    const handleOverlaySearch = useCallback(
        async (query: string): Promise<KbSearchResult[]> => {
            if (!query.trim()) return []
            return searchKb(axiosPrivate, query)
        },
        [axiosPrivate]
    )

    const toggleTheme = () => {
        dispatch(changeTheme(theme === 'dark' ? 'light' : 'dark'))
    }

    return (
        <div
            className="kb-wrapper"
            data-bs-theme={theme === 'dark' ? 'dark' : undefined}
        >
            <nav className="kb-navbar">
                <div className="kb-navbar__left">
                    {!isHome ? (
                        <button
                            className="kb-navbar__back"
                            onClick={goHome}
                            title="Back to Knowledge Base home"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    ) : null}
                    <button onClick={goHome} className="kb-navbar__brand">
                        Knowledge Base
                    </button>
                </div>
                <div className="kb-navbar__right">
                    <button
                        className="kb-navbar__search-btn"
                        onClick={() => setSearchOverlayOpen(true)}
                        title="Search (⌘K)"
                    >
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <span className="d-none d-md-inline ms-2">Search</span>
                        <kbd className="d-none d-lg-inline ms-2">⌘K</kbd>
                    </button>
                    <button
                        className="kb-navbar__theme-btn"
                        onClick={toggleTheme}
                        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                    >
                        <FontAwesomeIcon
                            icon={theme === 'dark' ? faSun : faMoon}
                        />
                    </button>
                    <Link to="/" className="kb-navbar__cms-link">
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            className="me-1"
                        />
                        <span className="d-none d-sm-inline">Back to CMS</span>
                    </Link>
                </div>
            </nav>

            <main className="kb-main">
                <Routes>
                    <Route
                        index
                        element={
                            <HomePage
                                onSelectCategory={selectCategory}
                                onSelectArticle={selectArticle}
                                onSearchSubmit={handleSearchSubmit}
                                onOpenSearch={() => setSearchOverlayOpen(true)}
                            />
                        }
                    />
                    <Route
                        path="category/:slug"
                        element={
                            <CategoryPage
                                onBack={goHome}
                                onSelectArticle={selectArticle}
                            />
                        }
                    />
                    <Route
                        path="article/:slug"
                        element={
                            <ArticlePage
                                onGoHome={goHome}
                                onSelectArticle={selectArticle}
                            />
                        }
                    />
                    <Route
                        path="search"
                        element={
                            <SearchPage
                                onBack={goHome}
                                onSelectArticle={selectArticle}
                            />
                        }
                    />
                </Routes>
            </main>

            <KBFooter />

            <SearchOverlay
                isOpen={searchOverlayOpen}
                onClose={() => setSearchOverlayOpen(false)}
                onSelectArticle={selectArticle}
                onSearch={handleOverlaySearch}
            />
        </div>
    )
}

export default KnowledgeBase
